import React, { useEffect, useMemo, useState } from 'react';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartSimple,
    faMusic,
    faPlay,
    faChevronRight,
    faCircleArrowLeft,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import User from '../models/User';

const formatViewerName = (profile) => {
    if (!profile) return null;
    const first = (profile.firstName || '').trim();
    const last = (profile.lastName || '').trim();
    if (first && last) return `${first} ${last}`;
    return first || last || null;
};

// Project + track insights. iOS writes plays at
// `/artists/{aid}/projects/{pid}/tracks/{trackId}/revisions/{revId}/metadata/plays/{uid}/count`
// (incremented per play). We pull the whole `tracks` subtree once and
// aggregate locally so a single round-trip drives both the project list
// and the per-track drill-down.
const ProjectInsights = ({ project, onClose }) => {
    const [trackData, setTrackData] = useState(null);
    const [error, setError] = useState('');
    const [drillTrackId, setDrillTrackId] = useState(null);
    const [profiles, setProfiles] = useState({});
    const [activeTab, setActiveTab] = useState('tracks'); // tracks | listeners
    const [trackSort, setTrackSort] = useState('order');  // order | plays

    const artistId = project?.artist?.id || project?.artistId;
    const projectId = project?.id;

    useEffect(() => {
        if (!project) return;
        let cancelled = false;
        firebase.database()
            .ref('artists').child(artistId)
            .child('projects').child(projectId)
            .child('tracks').once('value')
            .then((snap) => {
                if (cancelled) return;
                const raw = snap.val() || {};
                const tracks = Object.entries(raw).map(([trackId, t]) => {
                    const meta = (t && t.metadata) || {};
                    const currRevId = meta['current revision'];
                    const rev = currRevId && t.revisions ? t.revisions[currRevId] : null;
                    const playsMap = (rev && rev.metadata && rev.metadata.plays) || {};
                    const listeners = Object.entries(playsMap).map(([uid, info]) => ({
                        uid,
                        count: (info && info.count) || 0,
                    })).filter((l) => l.count > 0);
                    listeners.sort((a, b) => b.count - a.count);
                    const totalPlays = listeners.reduce((sum, l) => sum + l.count, 0);
                    return {
                        trackId,
                        name: meta.name || 'Untitled',
                        orderWeight: meta['order weight'] || 0,
                        currRevId,
                        totalPlays,
                        listeners,
                    };
                }).sort((a, b) => (a.orderWeight || 0) - (b.orderWeight || 0));
                setTrackData(tracks);
                setError('');
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || "Couldn't load insights.");
                setTrackData([]);
            });
        return () => { cancelled = true; };
    }, [artistId, projectId, project]);

    // Resolve every uid that's appeared as a listener (across all tracks) so
    // names are ready by the time the user drills into any track.
    useEffect(() => {
        if (!trackData) return;
        const allUids = new Set();
        for (const t of trackData) {
            for (const l of t.listeners) allUids.add(l.uid);
        }
        let cancelled = false;
        allUids.forEach((uid) => {
            if (profiles[uid] !== undefined) return;
            User.getPublicProfile(uid).then((u) => {
                if (cancelled) return;
                setProfiles((prev) => ({ ...prev, [uid]: u || null }));
            }).catch(() => {
                if (cancelled) return;
                setProfiles((prev) => ({ ...prev, [uid]: null }));
            });
        });
        return () => { cancelled = true; };
    }, [trackData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const totalProjectPlays = useMemo(() => {
        if (!trackData) return 0;
        return trackData.reduce((sum, t) => sum + t.totalPlays, 0);
    }, [trackData]);

    // Sorted view of trackData driven by the active sort mode. Default is
    // the project's track order (load order preserved by useEffect's sort);
    // "plays" surfaces the most-played first.
    const sortedTracks = useMemo(() => {
        if (!trackData) return [];
        if (trackSort === 'plays') {
            return [...trackData].sort((a, b) => b.totalPlays - a.totalPlays);
        }
        return trackData;
    }, [trackData, trackSort]);

    // Aggregate plays per listener across every track in the project.
    const aggregateListeners = useMemo(() => {
        if (!trackData) return [];
        const totals = new Map();
        for (const t of trackData) {
            for (const l of t.listeners) {
                totals.set(l.uid, (totals.get(l.uid) || 0) + l.count);
            }
        }
        return Array.from(totals.entries())
            .map(([uid, count]) => ({ uid, count }))
            .sort((a, b) => b.count - a.count);
    }, [trackData]);

    const drillTrack = drillTrackId
        ? trackData?.find((t) => t.trackId === drillTrackId)
        : null;

    if (drillTrack) {
        return (
            <div className="modal-backdrop" onClick={onClose}>
                <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-eyebrow eyebrow-share">
                        <FontAwesomeIcon icon={faChartSimple} />
                        <span>TRACK INSIGHTS</span>
                    </div>
                    <div className="modal-title">{drillTrack.name}</div>
                    <div className="insights-summary">
                        <div className="insights-summary-stat">
                            <FontAwesomeIcon icon={faPlay} />
                            <span className="insights-stat-num">{drillTrack.totalPlays}</span>
                            <span className="insights-stat-label">
                                play{drillTrack.totalPlays === 1 ? '' : 's'}
                            </span>
                        </div>
                        <div className="insights-summary-stat">
                            <FontAwesomeIcon icon={faUser} />
                            <span className="insights-stat-num">{drillTrack.listeners.length}</span>
                            <span className="insights-stat-label">
                                listener{drillTrack.listeners.length === 1 ? '' : 's'}
                            </span>
                        </div>
                    </div>

                    {drillTrack.listeners.length === 0 ? (
                        <div className="share-empty">No plays yet.</div>
                    ) : (
                        <div className="share-link-list">
                            {drillTrack.listeners.map((l) => {
                                const name = formatViewerName(profiles[l.uid]);
                                return (
                                    <div key={l.uid} className="share-link-row">
                                        <span className="share-link-row-icon icon-blue">
                                            <FontAwesomeIcon icon={faUser} />
                                        </span>
                                        <div className="share-link-row-main">
                                            <div className="share-link-row-title">
                                                {name ? (
                                                    name
                                                ) : profiles[l.uid] === undefined ? (
                                                    <span className="skeleton-line viewer-name-skeleton" />
                                                ) : (
                                                    <span className="viewer-name-unknown">Unknown listener</span>
                                                )}
                                            </div>
                                            <div className="share-link-row-sub">
                                                {l.count} play{l.count === 1 ? '' : 's'}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="new-project-actions">
                        <button className="btn-ghost" onClick={() => setDrillTrackId(null)}>
                            <FontAwesomeIcon icon={faCircleArrowLeft} /> Back
                        </button>
                        <button className="btn-primary" onClick={onClose}>Done</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-eyebrow eyebrow-share">
                    <FontAwesomeIcon icon={faChartSimple} />
                    <span>INSIGHTS</span>
                </div>
                <div className="modal-title">{project?.name || 'Untitled'}</div>

                <div className="insights-summary">
                    <div className="insights-summary-stat">
                        <FontAwesomeIcon icon={faPlay} />
                        <span className="insights-stat-num">{totalProjectPlays}</span>
                        <span className="insights-stat-label">
                            total play{totalProjectPlays === 1 ? '' : 's'}
                        </span>
                    </div>
                    <div className="insights-summary-stat">
                        <FontAwesomeIcon icon={faMusic} />
                        <span className="insights-stat-num">{trackData?.length || 0}</span>
                        <span className="insights-stat-label">
                            track{(trackData?.length || 0) === 1 ? '' : 's'}
                        </span>
                    </div>
                </div>

                {error && <div className="new-project-error">{error}</div>}

                {trackData === null ? (
                    <div className="share-empty">Loading…</div>
                ) : trackData.length === 0 ? (
                    <div className="share-empty">No tracks yet.</div>
                ) : (
                    <>
                        <div className="insights-tabs">
                            <button
                                type="button"
                                className={`insights-tab ${activeTab === 'tracks' ? 'is-active' : ''}`}
                                onClick={() => setActiveTab('tracks')}
                            >
                                Tracks
                            </button>
                            <button
                                type="button"
                                className={`insights-tab ${activeTab === 'listeners' ? 'is-active' : ''}`}
                                onClick={() => setActiveTab('listeners')}
                            >
                                Listeners
                                {aggregateListeners.length > 0 && (
                                    <span className="insights-tab-count">{aggregateListeners.length}</span>
                                )}
                            </button>
                        </div>

                        {activeTab === 'tracks' && (
                            <>
                                <div className="insights-sort-row">
                                    <span className="insights-sort-label">Sort</span>
                                    <div className="insights-sort-toggle">
                                        <button
                                            type="button"
                                            className={`insights-sort-option ${trackSort === 'order' ? 'is-active' : ''}`}
                                            onClick={() => setTrackSort('order')}
                                        >
                                            Order
                                        </button>
                                        <button
                                            type="button"
                                            className={`insights-sort-option ${trackSort === 'plays' ? 'is-active' : ''}`}
                                            onClick={() => setTrackSort('plays')}
                                        >
                                            Most played
                                        </button>
                                    </div>
                                </div>
                                <div className="share-link-list">
                                    {sortedTracks.map((t) => (
                                        <button
                                            key={t.trackId}
                                            className="share-link-row insights-track-row"
                                            onClick={() => setDrillTrackId(t.trackId)}
                                            type="button"
                                        >
                                            <div className="share-link-row-main">
                                                <div className="share-link-row-title">{t.name}</div>
                                                <div className="share-link-row-sub">
                                                    {t.totalPlays} play{t.totalPlays === 1 ? '' : 's'}
                                                    {t.listeners.length > 0 && ` · ${t.listeners.length} listener${t.listeners.length === 1 ? '' : 's'}`}
                                                </div>
                                            </div>
                                            <FontAwesomeIcon icon={faChevronRight} className="share-action-arrow" />
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {activeTab === 'listeners' && (
                            aggregateListeners.length === 0 ? (
                                <div className="share-empty">No plays yet.</div>
                            ) : (
                                <div className="share-link-list">
                                    {aggregateListeners.map((l) => {
                                        const name = formatViewerName(profiles[l.uid]);
                                        return (
                                            <div key={l.uid} className="share-link-row">
                                                <span className="share-link-row-icon icon-blue">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </span>
                                                <div className="share-link-row-main">
                                                    <div className="share-link-row-title">
                                                        {name ? (
                                                            name
                                                        ) : profiles[l.uid] === undefined ? (
                                                            <span className="skeleton-line viewer-name-skeleton" />
                                                        ) : (
                                                            <span className="viewer-name-unknown">Unknown listener</span>
                                                        )}
                                                    </div>
                                                    <div className="share-link-row-sub">
                                                        {l.count} play{l.count === 1 ? '' : 's'} across the project
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </>
                )}

                <div className="new-project-actions">
                    <button className="btn-ghost" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
};

export default ProjectInsights;
